<template>
  <div class="flex flex-col gap-2 justify-start items-start">
    <!-- Expert Profile Card -->
    <div
      class="w-[315px] bg-white border border-[#E5E7EB] rounded-lg p-3 flex flex-col gap-2.5"
      style="font-family: Inter, sans-serif"
    >
      <!-- Main content container -->
      <div class="flex flex-col gap-3 w-full">
        <!-- Fixed height container matching Figma -->
        <div class="flex flex-col gap-3 h-[68px]">
          <!-- Row 1: Profile + Name/LinkedIn vs Skill badges -->
          <div class="flex justify-between items-start w-full">
            <!-- Left: Profile image + Name + LinkedIn -->
            <div class="flex items-center gap-2.5">
              <!-- Profile image -->
              <div
                class="w-[32.46px] h-[32.46px] rounded-[15.57px] overflow-hidden flex-shrink-0"
              >
                <img
                  :src="expertProfilePicture"
                  :alt="`${expertName} Profile`"
                  class="w-full h-full object-cover"
                />
              </div>
              <!-- Name, title, and LinkedIn icon -->
              <div class="flex flex-col gap-0.5">
                <!-- Name + LinkedIn icon (same line) -->
                <div class="flex items-center gap-1">
                  <div
                    class="text-[#2C2C2C] font-semibold text-[14px] leading-[1.67]"
                  >
                    {{ expertName }}
                  </div>
                  <!-- LinkedIn icon right next to name -->
                  <a
                    v-if="linkedinProfile"
                    @click="handleLinkedInClick"
                    class="text-[#0076B2] w-[14px] h-[17px] flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer"
                  >
                    <svg
                      width="14"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      class="w-full h-full"
                    >
                      <path
                        d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
                      />
                    </svg>
                  </a>
                </div>
                <!-- Title -->
                <div
                  class="text-[#6B7280] font-normal text-[12px] leading-tight"
                >
                  {{ expertTitle }}
                </div>
              </div>
            </div>

            <!-- Right: Fire badge (if expert has past meetings) -->
            <div class="flex items-center gap-[2.57px]">
              <!-- Fire badge - shown only if expert has past meetings -->
              <div
                v-if="anyPastMeetings"
                class="relative flex flex-col rounded-md p-0.5 items-center bg-violet-100 group"
              >
                <div v-html="FIRE_ICON"></div>
                <!-- Tooltip -->
                <div
                  class="absolute left-1/2 -translate-x-[80%] top-full mt-2 -ml-4 w-max max-w-xs px-2 py-1 text-xs text-white bg-gray-900 rounded-md opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-50 pointer-events-none"
                >
                  Recent Meetings
                </div>
              </div>
            </div>
          </div>

          <!-- Row 2: Country flag and spoken languages -->
          <div class="flex justify-start w-full">
            <div class="flex items-center gap-[3.49px]">
              <!-- Country flag based on expert's country -->
              <div
                v-if="getCountryCode(expertCountry)"
                class="relative w-[26px] h-[19px] bg-[#F6F8FA] rounded-[4.08px] flex justify-center items-center group"
              >
                <span
                  :class="`fi fi-${getCountryCodeForFlagIcon(expertCountry)}`"
                  class="text-sm"
                ></span>
                <div
                  class="absolute left-1/2 top-full mt-1 -ml-4 w-max max-w-xs px-2 py-1 text-xs text-white bg-gray-900 rounded-md opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-50 pointer-events-none -translate-x-1/2"
                >
                  Location: {{ expertCountry }}
                </div>
              </div>
              <!-- Spoken languages (additional tags) -->
              <template v-if="spokenLanguages.length > 0">
                <div
                  v-for="(language, index) in spokenLanguages.slice(0, 2)"
                  :key="`lang-${index}`"
                  class="bg-[#F6F8FA] rounded-[4.08px] flex justify-center items-center px-2 py-1"
                  :title="language"
                >
                  <span
                    class="text-[#2C2C2C] font-normal text-[10px] leading-tight text-center whitespace-nowrap"
                  >
                    {{ language }}
                  </span>
                </div>
              </template>
              <!-- Default US flag if no country available -->
              <template v-if="!getCountryCode(expertCountry)">
                <div
                  class="relative w-[26px] h-[19px] bg-[#F6F8FA] rounded-[4.08px] flex justify-center items-center group"
                >
                  <span class="fi fi-us text-sm"></span>
                  <div
                    class="absolute left-1/2 top-full mt-1 -ml-4 w-max max-w-xs px-2 py-1 text-xs text-white bg-gray-900 rounded-md opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-50 pointer-events-none -translate-x-1/2"
                  >
                    Location: United States
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- Progress bars section -->
        <div class="flex flex-col gap-1.5 w-full mt-4">
          <!-- Expertise progress bar -->
          <div class="flex items-center gap-0.5 w-full">
            <div
              class="text-black font-normal text-xs leading-tight w-[76px] flex-shrink-0"
            >
              Expertise
            </div>
            <div class="flex items-stretch gap-1 flex-1 h-2">
              <div
                v-for="i in 5"
                :key="`expertise-${i}`"
                class="flex-1 rounded-sm"
                :class="i <= expertiseLevel ? 'bg-[#6A48F3]' : 'bg-[#C7D3EB]'"
              ></div>
            </div>
          </div>

          <!-- Experience progress bar -->
          <div class="flex items-center gap-0.5 w-full">
            <div
              class="text-black font-normal text-xs leading-tight w-[76px] flex-shrink-0"
            >
              Experience
            </div>
            <div class="flex items-stretch gap-1 flex-1 h-2">
              <div
                v-for="i in 5"
                :key="`experience-${i}`"
                class="flex-1 rounded-sm"
                :class="i <= experienceLevel ? 'bg-[#6A48F3]' : 'bg-[#C7D3EB]'"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-wrap gap-2 mt-6">
      <div v-html="CLOCK_ICON"></div>
      <span class="text-gray-900/60 font-sans text-base font-bold leading-6"
        >30 min</span
      >
    </div>
    <div class="flex flex-wrap gap-2">
      <div v-html="PHONE_ICON"></div>
      <span class="text-gray-900/60 font-sans text-base font-bold leading-6"
        >Phone call</span
      >
    </div>
    <div class="flex flex-row gap-2" v-if="hasDateAndTime">
      <div v-html="CALENDAR_ICON"></div>
      <span class="text-gray-900/60 font-sans text-base font-bold leading-6">{{
        dateAndTime
      }}</span>
    </div>

    <!-- old booking -->
    <div class="flex flex-col gap-2 mt-4" v-if="previousMeeting">
      <span
        class="text-primary-purple bg-primary-purple/10 px-2 py-1 rounded-md font-sans text-base font-bold leading-6"
        >Previous booking:</span
      >
      <div class="flex flex-row items-center gap-2">
        <div v-html="CALENDAR_ICON"></div>
        <span
          class="text-gray-900/60 font-sans text-base font-bold leading-6"
          >{{ previousMeeting }}</span
        >
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { CLOCK_ICON, PHONE_ICON } from "@/assets/svg/shared/svg-constants";
import { COMPANY_LOGO, CALENDAR_ICON } from "@/assets/svg/shared/svg-constants";
import { computed, defineComponent, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { gql } from "@apollo/client/core";
import { useQuery } from "@vue/apollo-composable";
import { useSlugQueries } from "@/composables/useSlugQueries";
import { getCountryCode } from "@/utils/countryUtils";
import { FIRE_ICON } from "@/assets/svg/shared/svg-constants";

const GET_EXPERT_BY_UUID = gql`
  query getExpertByUuid($uuid: String!) {
    getExpertByUuid(uuid: $uuid) {
      firstName
      lastName
      title
      profilePicture
      linkedinProfile
      spokenLanguages
      country
      anyPastMeetings
    }
  }
`;

const FETCH_PUBLIC_USER_PROGRAM_PROFILE = gql`
  query fetchPublicUserProgramProfile(
    $userSlug: String
    $userUuid: String
    $programSlug: String
    $programUuid: String
  ) {
    fetchPublicUserProgramProfile(
      userSlug: $userSlug
      userUuid: $userUuid
      programSlug: $programSlug
      programUuid: $programUuid
    ) {
      selectedExpertTags
      expertise
      experience
    }
  }
`;

export default defineComponent({
  setup() {
    const route = useRoute();
    const previousMeeting = ref("");

    // Use the slug queries composable
    const { isSlugBasedRoute, expertBySlugResult, expertSlug, programSlug } =
      useSlugQueries();

    // Fetch expert profile using GraphQL (legacy query parameter routing)
    const expertUuid = computed(() => route.query.expert as string);
    const programUuid = computed(() => route.query.program as string);

    const { result: expertResult } = useQuery(
      GET_EXPERT_BY_UUID,
      () => ({ uuid: expertUuid.value }),
      () => ({ enabled: !!expertUuid.value && !isSlugBasedRoute.value })
    );

    // Fetch program profile data with smart slug/UUID detection
    const { result: programProfileResult } = useQuery(
      FETCH_PUBLIC_USER_PROGRAM_PROFILE,
      () => {
        // Prefer slugs if available, otherwise use UUIDs
        if (isSlugBasedRoute.value && expertSlug.value && programSlug.value) {
          return {
            userSlug: expertSlug.value,
            programSlug: programSlug.value,
            userUuid: null,
            programUuid: null,
          };
        } else if (expertUuid.value && programUuid.value) {
          return {
            userSlug: null,
            programSlug: null,
            userUuid: expertUuid.value,
            programUuid: programUuid.value,
          };
        }
        return null;
      },
      () => ({
        enabled: !!(
          (isSlugBasedRoute.value && expertSlug.value && programSlug.value) ||
          (!isSlugBasedRoute.value && expertUuid.value && programUuid.value)
        ),
      })
    );

    // Consolidated expert data with fallback logic
    const expertData = computed(() => {
      // Use slug-based data if available
      if (expertBySlugResult.value?.getExpertBySlug) {
        return expertBySlugResult.value.getExpertBySlug;
      }
      // Fallback to legacy query parameter routing
      if (expertResult.value?.getExpertByUuid) {
        return expertResult.value.getExpertByUuid;
      }
      return null;
    });

    const expertName = computed(() => {
      if (expertData.value) {
        const { firstName, lastName } = expertData.value;
        return `${firstName} ${lastName}`;
      }
      return "Expert"; // fallback to default text
    });

    const expertProfilePicture = computed(() => {
      return expertData.value?.profilePicture || null;
    });

    const expertTitle = computed(() => {
      return expertData.value?.title || "Expert";
    });

    const linkedinProfile = computed(() => {
      return expertData.value?.linkedinProfile || null;
    });

    // Program profile data
    const programProfileData = computed(() => {
      return programProfileResult.value?.fetchPublicUserProgramProfile || null;
    });

    const expertiseLevel = computed(() => {
      return programProfileData.value?.expertise || 0;
    });

    const experienceLevel = computed(() => {
      return programProfileData.value?.experience || 0;
    });

    const spokenLanguages = computed(() => {
      return expertData.value?.spokenLanguages || [];
    });

    const expertCountry = computed(() => {
      return expertData.value?.country || "";
    });

    const anyPastMeetings = computed(() => {
      return expertData.value?.anyPastMeetings || false;
    });

    // Function to get country code for flag icon (using the correct approach)
    const getCountryCodeForFlagIcon = (country: string) => {
      const countryCode = getCountryCode(country) || "US";
      return countryCode.toLowerCase();
    };

    const formatDate = (dateString: string): string => {
      const [year, month, day] = dateString.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      // Define options for the date format
      const options: Intl.DateTimeFormatOptions = {
        weekday: "long", // Full weekday name (e.g., "Monday")
        month: "short", // Short month name (e.g., "Nov")
        day: "numeric", // Day of the month (e.g., "18")
        year: "numeric", // Full year (e.g., "2024")
      };

      return new Intl.DateTimeFormat("en-US", options).format(date);
    };

    const formatTimeRange = (timeString: string): string => {
      // Create a Date object for the starting time (8:00 PM)
      const [hour, minute] = timeString.split(":").map(Number);
      const startTime = new Date();
      startTime.setHours(hour, minute, 0, 0);

      // Calculate the ending time (8:30 PM)
      const endTime = new Date(startTime.getTime() + 30 * 60000); // Add 30 minutes

      // Format both times
      const options: Intl.DateTimeFormatOptions = {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      };

      const startFormatted = new Intl.DateTimeFormat("en-US", options).format(
        startTime
      );
      const endFormatted = new Intl.DateTimeFormat("en-US", options).format(
        endTime
      );

      return `${startFormatted} - ${endFormatted}`;
    };

    const hasDateAndTime = computed(() => {
      // Check for the presence of the query param (example: "userId")
      return route.query.date && route.query.startTime;
    });

    const dateAndTime = computed(() => {
      const date = route.query.date as string;
      const time = route.query.startTime as string;
      return `${formatTimeRange(time)}, ${formatDate(date)} `;
    });

    const checkPreviousMeeting = () => {
      if (route.query.oldBooking) {
        const current = route.query.oldBooking as string;
        const dateObject = new Date(current);

        const formattedDateTime = dateObject.toLocaleString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });

        previousMeeting.value = formattedDateTime;
      }
    };

    const handleLinkedInClick = () => {
      if (linkedinProfile.value) {
        let profileUrl = linkedinProfile.value;
        if (
          !profileUrl.startsWith("http://") &&
          !profileUrl.startsWith("https://")
        ) {
          profileUrl = `https://${profileUrl}`;
        }
        window.open(profileUrl, "_blank");
      }
    };

    onMounted(() => {
      checkPreviousMeeting();
    });

    return {
      CLOCK_ICON,
      PHONE_ICON,
      COMPANY_LOGO,
      CALENDAR_ICON,
      hasDateAndTime,
      dateAndTime,
      expertName,
      expertTitle,
      linkedinProfile,
      previousMeeting,
      expertProfilePicture,
      expertData,
      programProfileData,
      expertiseLevel,
      experienceLevel,
      spokenLanguages,
      expertCountry,
      anyPastMeetings,
      getCountryCode,
      getCountryCodeForFlagIcon,
      FIRE_ICON,
      handleLinkedInClick,
    };
  },
});
</script>
